import React, { FC, useState } from 'react';
import { IoChevronDown } from 'react-icons/io5';

import { Badge, Divider, Group, Stack, Text, UnstyledButton } from '@mantine/core';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';

import {
    all,
    Charger,
    ChargerConnector,
    Component,
    currentConverter,
    energyConverter,
    getApplicablePortFieldValue,
    powerConverter,
    TripCurves,
    VoltageType,
    voltageConverter,
} from 'models';
import { FormatHelpers } from 'helpers/formatters';

import { VoltageTypeSwitch } from 'components/buttons/VoltageTypeSwitch';

import { getComponentMergedChargingPorts, getComponentMergedPorts } from './helpers/get-component-merged-ports';

const NUMBER_SHOWN_PORTS = 3;

const ComponentOverviewHitDatasheet: FC<{ component: Component }> = ({ component }) => {
    const electrical = component.electrical ?? {};

    const ports = electrical.ports || [];
    const chargingPorts = 'chargingPorts' in electrical ? electrical.chargingPorts : [];

    const energyCapacity = 'energyCapacity' in electrical ? electrical.energyCapacity : null;

    const componentDefinition = ComponentInstanceHelpers.definition(component.type);
    const mergedPorts = getComponentMergedPorts(ports, {
        displayAsSinglePort: componentDefinition.ports.displayAsSinglePort,
    });
    const mergedChargingPorts = getComponentMergedChargingPorts(chargingPorts) || [];

    const [showExpand, setShowExpand] = useState(mergedPorts.length + mergedChargingPorts.length > NUMBER_SHOWN_PORTS);

    const shownPorts = showExpand ? mergedPorts.slice(0, NUMBER_SHOWN_PORTS) : mergedPorts;
    const shownChargingPorts = showExpand
        ? mergedChargingPorts.slice(0, NUMBER_SHOWN_PORTS - shownPorts.length)
        : mergedChargingPorts;

    if (ports.length === 0 && chargingPorts.length === 0 && !energyCapacity?.value) {
        return null;
    }

    const justOnePort = mergedPorts.length + mergedChargingPorts.length === 1;

    const parallelableCapacity = 'parallelableCapacity' in electrical ? electrical.parallelableCapacity : 1;

    return (
        <Stack gap="xs">
            <Stack gap="sm">
                {energyCapacity?.value && (
                    <Group gap="xs">
                        <Badge variant="outline" color="gray.6" radius="xs" px={6}>
                            Capacity
                        </Badge>
                        {FormatHelpers.formatMeasurement(energyCapacity, energyConverter)}
                        {parallelableCapacity > 1 ? `/unit (max ${parallelableCapacity}x units parallel)` : ''}
                    </Group>
                )}
                {shownPorts.map(({ port, index }) => (
                    <ComponentOverviewHitDatasheetPort
                        port={port}
                        // Merged port ranges keep the values of the last port in the range
                        portMeta={component.metadata?.electrical?.ports?.[Array.isArray(index) ? index[1] : index]}
                        index={index}
                        key={index.toString()}
                        justOne={justOnePort}
                        displayAsSinglePort={!!componentDefinition.ports.displayAsSinglePort}
                        componentType={component.type}
                    />
                ))}
                {shownChargingPorts.map(({ port, index }) => (
                    <ChargingPortSpecifications
                        port={port}
                        index={index}
                        key={index.toString()}
                        justOne={justOnePort}
                    />
                ))}
                {'canServeAs' in component && component.canServeAs.length > 0 && (
                    <Stack gap={4}>
                        <ComponentOverviewHitDatasheetTitle title="Can be used as" />
                        <Group gap={4}>
                            {component.canServeAs.map((componentType) => (
                                <Badge
                                    variant="outline"
                                    color="gray.6"
                                    radius="xs"
                                    px={6}
                                    size="xs"
                                    key={componentType}
                                >
                                    {componentType}
                                </Badge>
                            ))}
                        </Group>
                    </Stack>
                )}
            </Stack>
            {showExpand && (
                <Group gap={2} c="dimmed">
                    <UnstyledButton fz="sm" fw={600} onClick={() => setShowExpand(false)}>
                        More specifications
                    </UnstyledButton>
                    <IoChevronDown />
                </Group>
            )}
        </Stack>
    );
};

const CARD_TRIP_CURVES = new Set(
    TripCurves.options.map((option) => option.value).filter((value) => value !== 'custom'),
);

const ComponentOverviewHitDatasheetPort: FC<{
    port: Component['electrical']['ports'][number];
    portMeta?: any;
    index: number | [number, number];
    justOne?: boolean;
    displayAsSinglePort?: boolean;
    componentType: Component['type'];
}> = ({ port, portMeta, index, justOne, displayAsSinglePort, componentType }) => {
    const hasDC = 'DC' in port && port.DC.enabled;
    const hasAC = 'AC' in port && port.AC.enabled;

    if (!hasDC && !hasAC) return null;

    const subtitle: string[] = [];

    if (port.powerFlowDirection) {
        subtitle.push(FormatHelpers.formatPowerFlowDirection(port.powerFlowDirection, displayAsSinglePort));
    }

    if ('purpose' in port && port.purpose) {
        subtitle.push(all[port.purpose as keyof typeof all]?.name ?? port.purpose);
    }

    const tripCurves: string[] =
        'tripCurve' in port && Array.isArray(port.tripCurve)
            ? port.tripCurve.filter((curve: string) => CARD_TRIP_CURVES.has(curve))
            : [];

    const title = getPortTitle(index, justOne);

    return (
        <Stack gap={'xs'}>
            {(title || subtitle.length > 0) && (
                <ComponentOverviewHitDatasheetTitle title={title} subtitle={subtitle.join(' • ')} />
            )}

            {tripCurves.length > 0 && (
                <Group gap={4}>
                    {tripCurves.map((curve) => (
                        <Badge key={curve} variant="outline" color="gray.6" radius="xs" px={6} size="xs">
                            {curve}
                        </Badge>
                    ))}
                </Group>
            )}

            {hasDC && (
                <PortSpecificationLine
                    portValues={port['DC']}
                    portMeta={portMeta?.DC}
                    voltageType={'DC'}
                    componentType={componentType}
                />
            )}
            {hasAC && (
                <PortSpecificationLine
                    portValues={port['AC']}
                    portMeta={portMeta?.AC}
                    voltageType={'AC'}
                    componentType={componentType}
                />
            )}
        </Stack>
    );
};

const ChargingPortSpecifications: FC<{
    port: Charger['electrical']['chargingPorts'][number];
    index: number | [number, number];
    justOne?: boolean;
}> = ({ port, index, justOne }) => {
    if (!port.voltageType) return null;

    const title = getPortTitle(index, justOne, 'Charging ');
    const subtitle = ChargerConnector.options.find((c) => port.connector === c.value)?.label || 'Unknown connector';

    return (
        <Stack gap={'xs'}>
            {title && <ComponentOverviewHitDatasheetTitle title={title} subtitle={subtitle} />}

            <PortSpecificationLine portValues={port} voltageType={port.voltageType} />
        </Stack>
    );
};

const ComponentOverviewHitDatasheetTitle = ({ title, subtitle }: { title: string | null; subtitle?: string }) => {
    return (
        <Divider
            label={
                <Text
                    inherit
                    fw={700}
                    fz="xs"
                    tt="uppercase"
                    style={{
                        lineHeight: '1rem',
                    }}
                >
                    {title}
                    {subtitle && (
                        <Text span tt="initial" fz="xs" c="gray.5">
                            {title && ' '}({subtitle})
                        </Text>
                    )}
                </Text>
            }
            labelPosition="left"
        />
    );
};

const PortSpecificationLine: FC<{
    portValues: any;
    portMeta?: any;
    voltageType: VoltageType;
    componentType?: Component['type'];
}> = ({ portValues, portMeta, voltageType, componentType }) => {
    const voltage =
        componentType === 'breaker'
            ? FormatHelpers.formatBreakerCardVoltage(portValues)
            : FormatHelpers.formatMinNomMax(portValues.voltage, voltageConverter);
    const power = FormatHelpers.formatMinNomMax(portValues.power, powerConverter);
    const current = FormatHelpers.formatMinNomMax(portValues.current, currentConverter);

    const specifications = [voltage, power, current].filter(Boolean);

    if (componentType === 'fuse') {
        const breakingCapacity = getApplicablePortFieldValue(portValues, portMeta, 'minimumBreakingCapacity');

        if (breakingCapacity) {
            specifications.push(`${FormatHelpers.formatCurrent(breakingCapacity)} breaking`);
        }
    }

    if (componentType === 'breaker') {
        const breakingCapacity =
            getApplicablePortFieldValue(portValues, portMeta, 'serviceShortCircuitBreakingCapacityIEC') ??
            getApplicablePortFieldValue(portValues, portMeta, 'ultimateShortCircuitBreakingCapacityIEC');

        if (breakingCapacity) {
            specifications.push(`${FormatHelpers.formatCurrent(breakingCapacity)} breaking`);
        }
    }

    const specificationsMissing = specifications.length === 0;

    return (
        <Group gap={'xs'} wrap="nowrap">
            <VoltageTypeSwitch voltageType={voltageType} voltageTypes={[voltageType]} variant={'outline'} />
            <Text c={specificationsMissing ? 'dimmed' : 'inherit'} fz="sm" lh={1.2}>
                {specificationsMissing ? `${voltageType} specifications missing` : specifications.join(' • ')}
            </Text>
        </Group>
    );
};

const getPortTitle = (index: number | [number, number], justOne?: boolean, prefix = '') => {
    const isPortRange = Array.isArray(index);
    const showTitle = !justOne || isPortRange;

    if (!showTitle) return null;

    return isPortRange ? `${prefix}Ports ${index[0] + 1} … ${index[1] + 1}` : `${prefix}Port ${index + 1}`;
};

export { ComponentOverviewHitDatasheet };
